/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * CustomCssSection — Accordion: per-view custom CSS, scoped at render time
 * to this view's content container. Mirrors the backend's
 * validate_custom_css() rules (arceeapp/public_view_helper.py) so the admin
 * gets immediate feedback instead of a round-trip 400 on save; the backend
 * remains the source of truth and re-validates on save regardless.
 */

import React from 'react';

const BLOCKED_PATTERNS = [
  { pattern: /@import/i, message: '"@import" is not allowed in custom CSS.' },
  {
    pattern: /position\s*:\s*(fixed|sticky)/i,
    message:
      '"position: fixed" and "position: sticky" are not allowed in custom CSS — ' +
      'container-scoping cannot contain them, so they could be used to overlay the whole page.',
  },
];

function findCustomCssError(cssText) {
  if (!cssText) return null;
  const hit = BLOCKED_PATTERNS.find(({ pattern }) => pattern.test(cssText));
  return hit ? hit.message : null;
}

const VOCAB = [
  ['.arcee-pub--card / --list / --table / --timeline', 'Root element — which layout is active'],
  ['.arcee-pub__title', 'Record title'],
  ['.arcee-pub__subtitle', 'Record subtitle'],
  ['.arcee-pub__desc', 'Record description'],
  ['.arcee-pub__badge', 'Status badge'],
  ['.arcee-pub__date', 'Date pill'],
  ['.arcee-pub__cost', 'Cost pill'],
  ['.arcee-pub__cta', 'Detail button'],
  ['.arcee-pub__thumb', 'Cover image (Card / List only)'],
  ['[data-record-id="…"]', 'Target one specific record'],
  ['[data-field="…"]', 'Target one table column (Table layout only)'],
  ['.arcee-pub__th', 'Table header cell (Table layout only)'],
  ['.arcee-pub__td', 'Table body cell (Table layout only)'],
];

export default function CustomCssSection({ config, updateConfig, theme }) {
  const value = config.custom_css || '';
  const error = findCustomCssError(value);

  function handleChange(e) {
    updateConfig({ custom_css: e.target.value });
  }

  return (
    <div>
      <div className="small text-secondary mb-2">
        Available classes and attributes — same names across every layout, so your CSS
        keeps working if you switch layouts later. Web fonts aren't supported — @font-face
        and @import rules are removed when the page renders. Use system font stacks.
      </div>
      <ul className="small mb-3" style={{ paddingLeft: 18 }}>
        {VOCAB.map(([selector, desc]) => (
          <li key={selector}>
            <code>{selector}</code> — {desc}
          </li>
        ))}
      </ul>
      <textarea
        className="form-control"
        rows={10}
        style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
        value={value}
        onChange={handleChange}
        aria-label="Custom CSS"
        aria-invalid={!!error}
        aria-describedby={error ? 'custom-css-error' : undefined}
        placeholder=".arcee-pub__title { font-weight: 900; }"
      />
      {error && (
        <div id="custom-css-error" role="alert" className="small mt-2" style={{ color: '#dc2626' }}>
          <i className="bi bi-exclamation-triangle me-1" />
          {error}
        </div>
      )}
    </div>
  );
}
